import React, { useEffect, useMemo, useRef, useState } from 'react';
import '@fontsource/nunito/400.css';
import '@fontsource/nunito/600.css';
import '@fontsource/nunito/700.css';
import '@fontsource/nunito/800.css';
import '@fontsource/nunito/900.css';
import { fetchJson } from '../apiBase';
import {
  BLUE_PAGE_BG,
  BLUE_SOFT_SURFACE,
  BLUE_PRIMARY_DARK,
} from './theme';
import Header from './sections/Header';
import { useParentNavigate } from './ParentNavigation';
import DashboardAccordion from './components/DashboardAccordion';
import { growthMetrics, todaySuggestions, turkeyDate, nextDashboardRefresh, TURKEY_TIME_ZONE, studyComparison, studyComparisonText } from './student360Rules';
import './Student360V2.css';
import { PARENT_MINI_TASK_STUDENT_HINT_KEY } from '../mini-tasks/parentMiniTaskSession';
import AttendanceBanner from './components/AttendanceBanner';
import AttendanceSummary from './sections/AttendanceSummary';
import PerformanceSummary from './sections/PerformanceSummary';
import SubjectPerformance from './sections/SubjectPerformance';
import ExamReport from './sections/ExamReport';
import CourseAssessmentReport from './sections/CourseAssessmentReport';
import CeteleReport from './sections/CeteleReport';
import StorybookEvaluationReport from './sections/StorybookEvaluationReport';
import ReadingSpeedReport from './sections/ReadingSpeedReport';
import SchoolExamReport from './sections/SchoolExamReport';
import StudySummary from './sections/StudySummary';
import LearningDevelopment from './sections/LearningDevelopment';
import FutureForestCard from './sections/FutureForestCard';
import Homeworks from './sections/Homeworks';
import NutritionAssessments from './sections/NutritionAssessments';
import { Card, Row, Skeleton } from './components/ui';
import { getStudentAvatar } from '../shared/studentAvatar';
import LegalFooter from '../components/LegalFooter';

const GARDEN_FLOWERS = ['🌼', '🌷', '🌻', '🌸', '🪻', '🌺'];
const SUGGESTION_PRESENTATION = {
  homework: { icon: '📝', title: 'Birlikte ödev kontrolü' },
  reading: { icon: '📖', title: 'Haftanın okuması' },
  study: { icon: '✏️', title: 'Bugünkü küçük adımlar' },
  book: { icon: '📚', title: 'Kitapla güzel bir hafta' },
};
const COMPACT_METRIC_VALUES = {
  'Bu hafta okuma atanmadı': 'Atanmadı',
  'Henüz kapsam dışı': 'Kapsam dışı',
  'Bilgi şu anda alınamıyor': 'Alınamadı',
};


const getStarPageUnit = (gradeLevel) => Math.max(1, Number(gradeLevel) || 1) * 50;

const calcLibraryStars = ({ pages = 0, gradeLevel = 1 }) =>
  Math.floor((Number(pages) || 0) / getStarPageUnit(gradeLevel));

const getLibraryLevelLabel = (stars) => {
  if (stars >= 7) return '🏆 Kitap Kurdu';
  if (stars >= 5) return '📚 Kitap Dostu';
  if (stars >= 3) return '📘 Okur';
  return '🌱 Okuma Yolunda';
};

const getLibraryBadge = (books) => {
  const b = Number(books) || 0;
  if (b >= 5) return { icon: '📚', label: 'Dost', mult: 3 };
  if (b >= 3) return { icon: '📖', label: 'Bitirici', mult: 2 };
  if (b === 2) return { icon: '📖', label: 'Bitirici', mult: 1 };
  return null;
};

const EXAM_TREND_UP = 'UP';
const EXAM_TREND_STABLE = 'STABLE';
const EXAM_TREND_DOWN = 'DOWN';
const EXAM_TREND_NULL = 'NULL';

const EXAM_OPENING_INITIAL = 'INITIAL';
const EXAM_OPENING_BALANCED = 'BALANCED';
const EXAM_OPENING_INCREASE_SMALL = 'INCREASE_SMALL';
const EXAM_OPENING_INCREASE_STRONG = 'INCREASE_STRONG';
const EXAM_OPENING_RECOVERY_UP = 'RECOVERY_UP';
const EXAM_OPENING_RECOVERY_DOWN = 'RECOVERY_DOWN';
const EXAM_OPENING_DECREASE_SMALL = 'DECREASE_SMALL';
const EXAM_OPENING_DECREASE = 'DECREASE';

const EXAM_ACTION_START = 'START';
const EXAM_ACTION_CONTINUE = 'CONTINUE';
const EXAM_ACTION_MAINTAIN = 'MAINTAIN';
const EXAM_ACTION_RECOVER = 'RECOVER';
const EXAM_ACTION_REVIEW_TOPIC = 'REVIEW_TOPIC';
const EXAM_ACTION_REVIEW_GENERAL = 'REVIEW_GENERAL';
const EXAM_ACTION_TIME_MANAGEMENT = 'TIME_MANAGEMENT';

const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizeExamLabel = (value) =>
  String(value || '')
    .trim()
    .normalize('NFKD')
    .toLowerCase();

const getExamDateValue = (value) => {
  const ts = new Date(value || 0).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

const formatInsightExamDate = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' });
};

const getExamSubjects = (exam) =>
  Array.isArray(exam?.subjects) ? exam.subjects.filter(Boolean) : [];

const getExamNetValue = (exam) => {
  const direct = toFiniteNumber(exam?.total_net);
  if (direct != null) return direct;
  const nets = getExamSubjects(exam)
    .map((subject) => toFiniteNumber(subject?.net))
    .filter((value) => value != null);
  if (!nets.length) return null;
  return nets.reduce((sum, value) => sum + value, 0);
};

const getSubjectTotal = (subject) => {
  const directKeys = ['toplam', 'total', 'total_questions', 'question_count'];
  for (const key of directKeys) {
    const direct = toFiniteNumber(subject?.[key]);
    if (direct != null && direct > 0) return direct;
  }
  const correct = toFiniteNumber(subject?.correct_answers ?? subject?.correct);
  const wrong = toFiniteNumber(subject?.wrong_answers ?? subject?.wrong);
  const blank = toFiniteNumber(subject?.blank_answers ?? subject?.blank) ?? 0;
  if (correct != null && wrong != null) return correct + wrong + blank;
  return null;
};

const mapExamSubjectMetrics = (subject) => {
  const label = String(subject?.name || subject?.subject || '').trim();
  const net = toFiniteNumber(subject?.net_score ?? subject?.net);
  const wrong = toFiniteNumber(subject?.wrong_answers ?? subject?.wrong);
  const blank = toFiniteNumber(subject?.blank_answers ?? subject?.blank) ?? 0;
  const total = getSubjectTotal(subject);
  return {
    raw: subject,
    label,
    normalizedLabel: normalizeExamLabel(label),
    net,
    wrong,
    blank,
    total,
    netRatio: net != null && total != null && total > 0 ? net / total : null,
    wrongRatio: wrong != null && total != null && total > 0 ? wrong / total : null,
    blankRatio: blank != null && total != null && total > 0 ? blank / total : null,
  };
};

const compareNormalizedExamLabels = (left, right) => {
  if (left.normalizedLabel < right.normalizedLabel) return -1;
  if (left.normalizedLabel > right.normalizedLabel) return 1;
  return 0;
};

const compareStrongSubjects = (left, right) => {
  const leftPrimary = left.netRatio ?? left.net ?? Number.NEGATIVE_INFINITY;
  const rightPrimary = right.netRatio ?? right.net ?? Number.NEGATIVE_INFINITY;
  if (leftPrimary !== rightPrimary) return rightPrimary - leftPrimary;
  const leftWrong = left.wrongRatio ?? Number.POSITIVE_INFINITY;
  const rightWrong = right.wrongRatio ?? Number.POSITIVE_INFINITY;
  if (leftWrong !== rightWrong) return leftWrong - rightWrong;
  const leftBlank = left.blankRatio ?? Number.POSITIVE_INFINITY;
  const rightBlank = right.blankRatio ?? Number.POSITIVE_INFINITY;
  if (leftBlank !== rightBlank) return leftBlank - rightBlank;
  return compareNormalizedExamLabels(left, right);
};

const compareSupportSubjects = (left, right) => {
  const leftPrimary = left.netRatio ?? left.net ?? Number.POSITIVE_INFINITY;
  const rightPrimary = right.netRatio ?? right.net ?? Number.POSITIVE_INFINITY;
  if (leftPrimary !== rightPrimary) return leftPrimary - rightPrimary;
  const leftWrong = left.wrongRatio ?? Number.NEGATIVE_INFINITY;
  const rightWrong = right.wrongRatio ?? Number.NEGATIVE_INFINITY;
  if (leftWrong !== rightWrong) return rightWrong - leftWrong;
  const leftBlank = left.blankRatio ?? Number.NEGATIVE_INFINITY;
  const rightBlank = right.blankRatio ?? Number.NEGATIVE_INFINITY;
  if (leftBlank !== rightBlank) return rightBlank - leftBlank;
  return compareNormalizedExamLabels(left, right);
};

const selectExamSubjectHighlights = (latestExam) => {
  const subjects = getExamSubjects(latestExam)
    .map(mapExamSubjectMetrics)
    .filter((subject) => subject.label && subject.net != null);
  if (!subjects.length) {
    return {
      subjects: [],
      strongSubject: null,
      supportSubject: null,
      highPerformance: false,
      showSupport: false,
    };
  }
  const strongSubject = subjects.slice().sort(compareStrongSubjects)[0] || null;
  const supportSubject = subjects.length >= 2
    ? subjects
      .filter((subject) => subject !== strongSubject)
      .sort(compareSupportSubjects)[0] || null
    : null;
  const highPerformance = subjects.every((subject) => subject.netRatio != null && subject.netRatio >= 0.70);
  return {
    subjects,
    strongSubject,
    supportSubject,
    highPerformance,
    showSupport: subjects.length >= 2 && !highPerformance,
  };
};

const findMappedExamSubject = (exam, normalizedLabel) => {
  if (!normalizedLabel) return null;
  return getExamSubjects(exam)
    .map(mapExamSubjectMetrics)
    .find((subject) => subject.label && subject.net != null && subject.normalizedLabel === normalizedLabel) || null;
};

const buildBestWorstSubjects = ({ latestExam, previousExam }) => {
  const { strongSubject, supportSubject, showSupport } = selectExamSubjectHighlights(latestExam);
  if (!strongSubject) return null;

  const buildCard = (subject) => {
    if (!subject) return null;
    const previousSubject = findMappedExamSubject(previousExam, subject.normalizedLabel);
    return {
      name: subject.label,
      value: subject.net,
      delta: previousSubject && previousSubject.net != null ? subject.net - previousSubject.net : null,
    };
  };

  return {
    best: buildCard(strongSubject),
    worst: showSupport ? buildCard(supportSubject) : null,
  };
};

const deriveExamTrendSignal = (examsAll, previousExam) => {
  if (!previousExam || !Array.isArray(examsAll) || examsAll.length < 3) return EXAM_TREND_NULL;
  const history = examsAll.slice().sort((a, b) => getExamDateValue(b?.date || b?.exam_date) - getExamDateValue(a?.date || a?.exam_date));
  const olderExam = history[2];
  const previousNet = getExamNetValue(previousExam);
  const olderNet = getExamNetValue(olderExam);
  if (previousNet == null || olderNet == null) return EXAM_TREND_NULL;
  const delta = previousNet - olderNet;
  if (Math.abs(delta) < 1) return EXAM_TREND_STABLE;
  return delta > 0 ? EXAM_TREND_UP : EXAM_TREND_DOWN;
};

const deriveExamOpening = ({ firstExam, delta, trend }) => {
  if (firstExam) return EXAM_OPENING_INITIAL;
  if (delta == null) return EXAM_OPENING_BALANCED;
  if (Math.abs(delta) < 1) return EXAM_OPENING_BALANCED;
  if (delta >= 1 && delta < 3) return trend === EXAM_TREND_DOWN ? EXAM_OPENING_RECOVERY_UP : EXAM_OPENING_INCREASE_SMALL;
  if (delta >= 3) return trend === EXAM_TREND_DOWN ? EXAM_OPENING_RECOVERY_UP : EXAM_OPENING_INCREASE_STRONG;
  if (delta <= -1 && delta > -3) return trend === EXAM_TREND_UP ? EXAM_OPENING_RECOVERY_DOWN : EXAM_OPENING_DECREASE_SMALL;
  return trend === EXAM_TREND_UP ? EXAM_OPENING_RECOVERY_DOWN : EXAM_OPENING_DECREASE;
};

const buildPerformanceInsight = ({ latestExam, previousExam, examsAll }) => {
  const {
    subjects,
    strongSubject,
    supportSubject,
    highPerformance,
    showSupport,
  } = selectExamSubjectHighlights(latestExam);
  if (!latestExam || !subjects.length) return null;

  const firstExam = !previousExam;
  const delta = firstExam ? null : (() => {
    const latestNet = getExamNetValue(latestExam);
    const previousNet = getExamNetValue(previousExam);
    if (latestNet == null || previousNet == null) return null;
    return latestNet - previousNet;
  })();
  const trend = deriveExamTrendSignal(examsAll, previousExam);
  const opening = deriveExamOpening({ firstExam, delta, trend });

  let action = EXAM_ACTION_REVIEW_GENERAL;
  if (firstExam) {
    action = EXAM_ACTION_START;
  } else if (highPerformance) {
    action = EXAM_ACTION_MAINTAIN;
  } else if (subjects.length <= 1) {
    if (trend === EXAM_TREND_UP) action = EXAM_ACTION_CONTINUE;
    else if (trend === EXAM_TREND_DOWN) action = EXAM_ACTION_RECOVER;
  } else if (supportSubject && supportSubject.wrongRatio != null && supportSubject.blankRatio != null) {
    if (supportSubject.wrongRatio > supportSubject.blankRatio) action = EXAM_ACTION_REVIEW_TOPIC;
    else if (supportSubject.blankRatio > supportSubject.wrongRatio) action = EXAM_ACTION_TIME_MANAGEMENT;
  }

  const openingTextMap = {
    [EXAM_OPENING_INITIAL]: 'Bu ilk deneme, mevcut tabloyu görmek için iyi bir başlangıç oldu.',
    [EXAM_OPENING_BALANCED]: 'Bu denemede genel görünüm dengeli ilerliyor.',
    [EXAM_OPENING_INCREASE_SMALL]: 'Bu denemede önceki sonuca göre olumlu bir ilerleme var.',
    [EXAM_OPENING_INCREASE_STRONG]: 'Bu denemede belirgin bir yükseliş dikkat çekiyor.',
    [EXAM_OPENING_RECOVERY_UP]: 'Önceki döneme göre yeniden toparlanma sinyali var.',
    [EXAM_OPENING_RECOVERY_DOWN]: 'Önceki iyi gidişe göre bu denemede daha dalgalı bir tablo var.',
    [EXAM_OPENING_DECREASE_SMALL]: 'Bu denemede küçük bir yavaşlama olsa da tablo toparlanabilir görünüyor.',
    [EXAM_OPENING_DECREASE]: 'Bu denemede önceki sonuca göre daha belirgin bir düşüş görülüyor.',
  };

  const actionTextMap = {
    [EXAM_ACTION_START]: 'Şimdilik en doğru adım, birkaç deneme daha gördükçe düzenli takibi sürdürmek.',
    [EXAM_ACTION_CONTINUE]: 'Mevcut çalışma ritmini korumak bu olumlu akışı destekleyebilir.',
    [EXAM_ACTION_MAINTAIN]: 'Mevcut güçlü seviyeyi korumak için aynı istikrarı sürdürmek yeterli olabilir.',
    [EXAM_ACTION_RECOVER]: 'Kısa ama düzenli tekrarlarla yeniden toparlanma sağlanabilir.',
    [EXAM_ACTION_REVIEW_TOPIC]: 'Özellikle konu tekrarına biraz daha ağırlık vermek faydalı olabilir.',
    [EXAM_ACTION_REVIEW_GENERAL]: 'Genel tekrar ve düzenli takip bu tabloyu destekleyebilir.',
    [EXAM_ACTION_TIME_MANAGEMENT]: 'Zaman kullanımı ve soru çözüm sırası üzerine küçük bir odak faydalı olabilir.',
  };

  let subjectText = '';
  if (strongSubject && showSupport && supportSubject) {
    subjectText = firstExam
      ? `${strongSubject.label} daha güçlü duruyor, ${supportSubject.label} başlangıçta biraz daha takip isteyebilir.`
      : `${strongSubject.label} güçlü görünüyor, ${supportSubject.label} biraz daha odak istiyor.`;
  } else if (strongSubject && highPerformance) {
    subjectText = `Genel tablo güçlü seyrediyor; özellikle ${strongSubject.label} öne çıkıyor.`;
  } else if (strongSubject) {
    subjectText = `${strongSubject.label} bu sınavda öne çıkan alan.`;
  }

  let tone = 'stable';
  if (opening === EXAM_OPENING_INITIAL) tone = 'initial';
  else if (action === EXAM_ACTION_MAINTAIN) tone = 'high_performance';
  else if ([EXAM_OPENING_INCREASE_SMALL, EXAM_OPENING_INCREASE_STRONG, EXAM_OPENING_RECOVERY_UP].includes(opening)) tone = 'improved';
  else if ([EXAM_OPENING_RECOVERY_DOWN, EXAM_OPENING_DECREASE_SMALL].includes(opening)) tone = 'warning';
  else if (opening === EXAM_OPENING_DECREASE) tone = 'dropped';

  return {
    tone,
    dateLabel: formatInsightExamDate(latestExam?.date || latestExam?.exam_date),
    summary: openingTextMap[opening] || openingTextMap[EXAM_OPENING_BALANCED],
    subjectText,
    actionText: actionTextMap[action] || actionTextMap[EXAM_ACTION_REVIEW_GENERAL],
  };
};

function ParentEngagementTopCard({
  study,
  comparison,
  studentName,
  hasTodayStudyLog,
  onOpenStudyLogModal,
  showStudyStats = true,
  today = turkeyDate(),
}) {
  const engagement = (study && typeof study.engagement === 'object' && study.engagement) ? study.engagement : {};
  const weekly = (engagement && typeof engagement.weekly === 'object' && engagement.weekly) ? engagement.weekly : {};
  const wow = (weekly && typeof weekly.wow === 'object' && weekly.wow) ? weekly.wow : {};

  const toInt = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : 0;
  };

  const monthlyDays = toInt(engagement?.monthly_active_days);
  const monthlyPoints = toInt(engagement?.monthly_vks);
  const streakCurrent = toInt(engagement?.streak_current_days);
  const streakBest = toInt(engagement?.streak_best_days);

  const now = new Date(`${today}T12:00:00Z`);
  const monthDayCount = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).getUTCDate();
  const monthlyDaysClamped = Math.max(0, Math.min(monthDayCount, monthlyDays));
  const progressWidth = `${Math.round((monthlyDaysClamped / Math.max(1, monthDayCount)) * 100)}%`;

  const streakBadge = streakCurrent >= 30
    ? '⭐ Alışkanlık ustası'
    : streakCurrent >= 14
      ? '🌳 Güçlü'
      : streakCurrent >= 7
        ? '🌿 Düzenli'
        : streakCurrent >= 3
          ? '🌱 Başlangıç'
          : '';


  const fmtSignedInt = (value) => {
    if (value == null) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    const rounded = Math.round(n);
    return `${rounded > 0 ? '+' : ''}${rounded}`;
  };

  const booksDelta = fmtSignedInt(wow?.completed_books_delta);
  const momentumText = `📈 ${studyComparisonText(comparison)}${booksDelta !== null ? ` Tamamlanan kitap ${booksDelta}.` : ''}`;

  const encouragementText = monthlyDays >= 25
    ? 'Neredeyse her gün eşlik ediliyor. Uzun vadede fark yaratır.'
    : monthlyDays >= 21
      ? 'Bu ay güçlü bir eşlik süreci var.'
      : monthlyDays >= 15
        ? 'Ayın yarısına yaklaşıldı. Bu istikrar çok değerli.'
        : monthlyDays >= 7
          ? 'İlk 7 gün tamamlandı. Düzenli takip alışkanlığı oluşuyor.'
          : null;

  const headingName = String(studentName || '').trim();
  const headingFirstName = headingName ? headingName.split(/\s+/)[0] : '';
  const heading = headingFirstName
    ? `🎯 ${headingFirstName} için bu ay katılım`
    : '🎯 Bu ay katılım';
  const progressMessage = monthlyDays >= 21
    ? 'Ritim çok güçlü, harika gidiyorsunuz!'
    : monthlyDays >= 10
      ? 'Ritim oluşuyor. Küçük adımlar çok değerli.'
      : monthlyDays >= 1
        ? 'Harika başlangıç!'
        : 'İlk küçük adım için güzel bir gün.';
  const todaySupportText = headingFirstName
    ? `Bugün ${headingFirstName} için güzel bir eşlik var.`
    : 'Bugün güzel bir eşlik var.';

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #dbeafe', borderTop: '3px solid #3b82f6', borderRadius: 12, padding: 12 }}>
      <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{heading}</div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 4 }}>
        <div style={{ fontSize: 36, fontWeight: 800, color: '#1e3a8a', lineHeight: 1.05 }}>
          {monthlyDays > 0 ? `${monthlyDays} Gün` : 'Henüz yok'}
        </div>
        <div style={{ fontSize: 13, color: '#1e3a8a', fontWeight: 800 }}>
          ⭐ {monthlyPoints} puan
        </div>
      </div>

      <div style={{ height: 16, borderRadius: 999, background: '#bcc9da', overflow: 'hidden', marginBottom: 6 }}>
        <div
          style={{
            width: progressWidth,
            height: '100%',
            borderRadius: 999,
            background: '#1d4ed8',
            transition: 'width 220ms ease',
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{monthlyDaysClamped} / {monthDayCount} gün</div>
      <div style={{ fontSize: 12, color: '#2563eb', marginBottom: 8, fontWeight: 700 }}>{progressMessage}</div>

      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, marginBottom: 2 }}>
          🔥 Seri
        </div>
        <div style={{ fontSize: 14, color: streakCurrent > 0 ? '#0f172a' : '#1f2937', fontWeight: streakCurrent > 0 ? 700 : 800, marginBottom: 2 }}>
          {streakCurrent > 0
            ? `${streakCurrent} gün${streakBadge ? ` · ${streakBadge}` : ''}`
            : 'Henüz başlamadı'}
        </div>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          {streakBest > 0 ? `En iyi seri: ${streakBest} gün` : 'En iyi seri: Henüz yok'}
        </div>
      </div>

      {!hasTodayStudyLog && onOpenStudyLogModal ? (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 6 }}>
            {headingFirstName
              ? `${headingFirstName} ile bugün küçük bir çalışma kaydı ekleyebilirsiniz.`
              : 'Bugün kısa bir çalışma kaydı ekleyebilirsiniz.'}
          </div>
          <button
            type="button"
            onClick={() => {
              if (typeof onOpenStudyLogModal === 'function') onOpenStudyLogModal();
            }}
            style={{
              border: '1px solid #bfdbfe',
              background: '#fff',
              color: '#1e3a8a',
              borderRadius: 10,
              padding: '8px 12px',
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.2,
              cursor: 'pointer',
            }}
          >
            🔵 Bugün kayıt ekle
          </button>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
          {todaySupportText}
        </div>
      )}

      {showStudyStats && <div style={{ fontSize: 12, color: '#475569', marginBottom: encouragementText ? 6 : 0 }}>
        {momentumText}
      </div>}

      {monthlyDays === 0 ? (
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: encouragementText ? 6 : 0 }}>
          Yeni ay başladı, küçük bir adımla başlayabilir.
        </div>
      ) : null}

      {encouragementText ? (
        <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid #dbeafe', background: '#eff6ff', color: '#1e3a8a', borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 700 }}>
          {encouragementText}
        </div>
      ) : null}
    </div>
  );
}

function ParentEngagementCommentCard({ study, studentName, canWrite = true, comparison }) {
  const engagement = (study && typeof study.engagement === 'object' && study.engagement) ? study.engagement : {};
  const firstName = String(studentName || '').trim().split(/\s+/)[0] || '';
  const toInt = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : 0;
  };
  const monthlyDays = toInt(engagement?.monthly_active_days);
  const wowRaw = comparison.questionsPct == null ? NaN : comparison.questionsPct;
  const hasWow = Number.isFinite(wowRaw);

  let comment = '';
  if (monthlyDays <= 0) {
    comment = !canWrite ? 'Henüz katılım kaydı yok.' : firstName
      ? `${firstName} ile ilk küçük adımı bugün atabilirsiniz.`
      : 'İlk küçük adımı bugün atabilirsiniz.';
  } else if (hasWow && wowRaw >= 15) {
    comment = firstName
      ? `${firstName} için son 7 günde ritim güçlenmiş görünüyor, harika bir ilerleme var.`
      : 'Son 7 günde ritim güçlenmiş görünüyor, harika bir ilerleme var.';
  } else if (hasWow && wowRaw <= -15) {
    comment = 'Önceki 7 güne göre ritim biraz yavaşladı; küçük ama düzenli adımlar yeniden toparlanmayı sağlar.';
  } else {
    comment = firstName
      ? `Her küçük adım, ${firstName} için öğrenmeyi güçlendirir.`
      : 'Küçük ama düzenli adımlar en güçlü gelişimi getirir.';
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
      <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>💬 Küçük bir not</div>
      <div style={{ fontSize: 13, color: '#475569' }}>
        {comment}
      </div>
    </div>
  );
}

export default function Student360() {
  const parentNavigate = useParentNavigate();
  // Theme is provided via theme.js; only using BLUE_PAGE_BG here

  const [context, setContext] = useState(null);
  const [entries, setEntries] = useState(null);
  const [pendingBooks, setPendingBooks] = useState(null);
  const [openPanel, setOpenPanel] = useState(null);
  const [studyMount, setStudyMount] = useState(null);
  const [booksMount, setBooksMount] = useState(null);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [observationLimit, setObservationLimit] = useState(5);
  const [errors, setErrors] = useState({});
  const [reloadToken, setReloadToken] = useState(0);
  const requestGeneration = useRef(0);

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [paymentSubscriptionIds, setPaymentSubscriptionIds] = useState([]);
  const [pointBalance, setPointBalance] = useState(null);
  const [overview, setOverview] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [observations, setObservations] = useState([]);
  const [starGarden, setStarGarden] = useState(null);
  const [metric, setMetric] = useState('score'); // 'score' | 'net' | 'rank'
  const [selectedExamId, setSelectedExamId] = useState('');
  const [subjectMetric, setSubjectMetric] = useState('net'); // 'correct' | 'wrong' | 'net'
  const [selectedGeneralEvalId, setSelectedGeneralEvalId] = useState('');
  const [selectedStorybookEvalId, setSelectedStorybookEvalId] = useState('');
  const [selectedReadingSpeedId, setSelectedReadingSpeedId] = useState('');
  const [studyLogModalRequestToken, setStudyLogModalRequestToken] = useState(0);
  const [bookModalRequestToken, setBookModalRequestToken] = useState(0);
  const refreshReference = context?.as_of;
  useEffect(() => {
    // A normal return mounts and fetches again; a browser back-cache restore does not.
    const refreshOnReturn = (event) => {
      if (event.persisted) setReloadToken((value) => value + 1);
    };
    window.addEventListener('pageshow', refreshOnReturn);
    return () => window.removeEventListener('pageshow', refreshOnReturn);
  }, []);
  useEffect(() => {
    if (!refreshReference) return undefined;
    // Use server time for the delay so the device clock cannot shift reminders.
    const delay = nextDashboardRefresh(refreshReference).getTime() - new Date(refreshReference).getTime();
    const deadline = Date.now() + delay;
    let timer;
    const refresh = () => {
      // Preserve an open form; refresh after it has been closed.
      if (document.querySelector('[role="dialog"]')) timer = window.setTimeout(refresh, 60000);
      else setReloadToken((value) => value + 1);
    };
    const resume = () => {
      if (document.visibilityState === 'visible' && Date.now() >= deadline) {
        window.clearTimeout(timer);
        refresh();
      }
    };
    timer = window.setTimeout(refresh, Math.max(1000, delay));
    document.addEventListener('visibilitychange', resume);
    return () => { window.clearTimeout(timer); document.removeEventListener('visibilitychange', resume); };
  }, [refreshReference]);


  useEffect(() => {
    if (window.location.pathname.startsWith('/admin/')) return undefined;
    let cancelled = false;
    fetchJson('/parent/dandu-payments/students').then(async (response) => {
      if (!response.ok) return;
      const body = await response.json();
      if (!cancelled) setPaymentSubscriptionIds((body.items || []).map((item) => String(item.student_id)));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!selectedId || window.location.pathname.startsWith('/admin/')) return undefined;
    let cancelled = false;
    fetchJson(`/parent/points?student_id=${encodeURIComponent(selectedId)}&limit=1`)
      .then(async (response) => {
        if (!response.ok) throw new Error('Puanlar yüklenemedi.');
        return response.json();
      })
      .then((data) => {
        if (!cancelled) setPointBalance({ studentId: String(selectedId), value: data.available_points });
      })
      .catch(() => {
        if (!cancelled) setPointBalance(null);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const readData = async (path) => {
    const response = await fetchJson(path);
    if (!response.ok) throw new Error('Bilgi şu anda alınamıyor.');
    return response.json();
  };
  const loadSnapshot = async (sid) => {
    const generation = requestGeneration.current;
    try {
      const body = await readData(`/parent/snapshot?student_id=${encodeURIComponent(sid)}`);
      if (generation === requestGeneration.current) setSnapshot(body.snapshot ?? body);
    } catch {
      if (generation === requestGeneration.current) setErrors((previous) => ({ ...previous, snapshot: true }));
    }
  };

  // Detect explicit student_id in query to support admin deep-linking
  const urlStudentId = useMemo(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const v = sp.get('student_id');
      return v ? String(v) : '';
    } catch {
      return '';
    }
  }, []);

  useEffect(() => {
    const generation = ++requestGeneration.current;
    const current = () => generation === requestGeneration.current;
    const load = async () => {
      setLoading(true);
      setOverview(null); setSnapshot(null); setContext(null); setStarGarden(null);
      setObservations([]); setEntries(null); setPendingBooks(null); setErrors({});
      setOpenPanel(null); setShowAllSuggestions(false); setObservationLimit(5);
      setSelectedExamId(''); setSelectedGeneralEvalId(''); setSelectedStorybookEvalId(''); setSelectedReadingSpeedId('');
      setStudyLogModalRequestToken(0); setBookModalRequestToken(0);
      try {
        let sid = urlStudentId;
        if (!sid) {
          const body = await readData('/parent/allowed-students');
          if (!current()) return;
          setStudents(body.items || []);
          sid = body.items?.length ? String(body.items[0].student_id) : '';
        }
        if (!current()) return;
        setSelectedId(sid);
        if (!sid) return;
        const query = `?student_id=${encodeURIComponent(sid)}`;
        await Promise.all([
          ['overview', '/parent/overview', setOverview, (body) => body],
          ['snapshot', '/parent/snapshot', setSnapshot, (body) => body.snapshot ?? body],
          ['context', '/parent/student360/context', setContext, (body) => body],
          ['observations', '/parent/observation-journal', setObservations, (body) => body.entries || []],
          ['stars', '/parent/star-garden', setStarGarden, (body) => ({ total: body.total, items: body.items || [] })],
        ].map(async ([name, path, setter, select]) => {
          try {
            const body = await readData(path + query);
            if (current()) setter(select(body));
          } catch {
            if (current()) setErrors((previous) => ({ ...previous, [name]: true }));
          }
        }));
      } catch {
        if (current()) setErrors({ overview: true });
      } finally {
        if (current()) setLoading(false);
      }
    };
    load();
    return () => { if (current()) requestGeneration.current += 1; };
  }, [urlStudentId, reloadToken]);

  useEffect(() => {
    if (!urlStudentId || window.location.pathname.startsWith('/admin/')) return undefined;
    let active = true;
    fetchJson('/parent/allowed-students')
      .then(async (response) => response.ok ? response.json() : null)
      .then((body) => { if (active) setStudents(body?.items || []); })
      .catch(() => {});
    return () => { active = false; };
  }, [urlStudentId]);

  if (loading) return <div style={{ background: BLUE_PAGE_BG, minHeight: '100vh', padding: 16 }} aria-label="Öğrenci bilgileri yükleniyor"><Skeleton width="100%" height={56} radius={12} /><Skeleton width="100%" height={140} radius={16} /></div>;

  const att = overview && overview.attendance_today;

  const exams = (snapshot && snapshot.exams_last_two) || [];
  const study = (snapshot && snapshot.study) || {};
  const forest = (snapshot && snapshot.forest) || null;
  const teacherSummary = (snapshot && snapshot.teacher_summary) || null;
  const weeklyPlan = (snapshot && snapshot.weekly_plan) || null;
  const homeworks = (snapshot && snapshot.homeworks) || [];
  const examsAll = (snapshot && snapshot.exams_all) || [];
  const courseAssessmentsAll = (snapshot && snapshot.general_evaluation_exams_all) || [];
  const storybookEvalAll = (snapshot && snapshot.storybook_read_evaluations_all) || [];
  const readingSpeedAll = (snapshot && snapshot.reading_speed_tests_all) || [];
  const schoolExamsAll = (snapshot && snapshot.school_exams_all) || [];
  const hasMockExamData = Array.isArray(examsAll) && examsAll.length > 0;
  const studentFirstName = String((overview && overview.student && overview.student.name) || '').trim();
  const hasTodayStudyLog = !!entries?.some((entry) => entry.date === context?.today);
  const verifiedBooks = snapshot ? (study.completed_books_all || study.completed_books || []) : null;
  const hasTodayCompletedBook = !!verifiedBooks?.some((book) => book.date === context?.today);
  const hasTodayContribution = !!entries?.some((entry) => entry.date === context?.today && [entry.questions, entry.pages, entry.minutes].some((value) => Number(value) > 0)) || hasTodayCompletedBook;
  const allSubjects = (() => {
    const set = new Set();
    for (const ex of (examsAll || [])) {
      for (const s of (ex.subjects || [])) {
        if (s && s.name) set.add(String(s.name));
      }
    }
    return Array.from(set);
  })();
  const displayedObservations = Array.isArray(observations) ? observations.slice(0, observationLimit) : [];
  const libraryView = (() => {
    const student = overview && overview.student ? overview.student : null;
    const studyObj = study || {};
    if (!student || !studyObj) return null;

    const fullName = `${student.name || ''} ${student.surname || ''}`.trim();
    const gradeLevel = context?.grade;

    // Use completed-book totals so numbers match Kütüphanem board.
    const pages = Number(studyObj?.completed_books_total_pages) || 0;
    const books = Number(studyObj?.completed_books_total_count)
      || (Array.isArray(studyObj?.completed_books) ? studyObj.completed_books.length : 0);
    const stars = gradeLevel ? calcLibraryStars({ pages, gradeLevel }) : null;
    const filledStars = Math.max(0, Math.min(7, stars));
    const overflowStars = Math.max(0, stars - 7);
    const badge = getLibraryBadge(books);

    return {
      fullName,
      gradeKnown: !!gradeLevel,
      pages,
      books,
      filledStars,
      overflowStars,
      levelLabel: gradeLevel ? getLibraryLevelLabel(stars) : 'Sınıf bilgisi alınamıyor',
      badge,
      avatar: getStudentAvatar(student.id, student.avatar_id, student.starter_emoji),
    };
  })();


  const latestExam = (exams && exams.length > 0) ? exams[0] : (overview && overview.last_exam ? overview.last_exam : null);
  const latestDate = latestExam ? (latestExam.date || latestExam.exam_date || '-') : '-';
  const formatDate = (d) => {
    if (!d || d === '-') return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString('tr-TR', { timeZone: TURKEY_TIME_ZONE });
  };

  const formatObservationDate = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return String(value);
    return dt.toLocaleString('tr-TR', { timeZone: TURKEY_TIME_ZONE, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // Exam selection is handled inside ExamReport section

  // Build series per selected metric with graceful fallbacks, using all exams when possible
  const buildSeries = (kind) => {
    // Helper: return a shallow-sorted copy by date (ascending)
    const sortAscByDate = (arr) => {
      const safe = (arr || []).slice();
      safe.sort((a, b) => {
        const da = new Date(a?.date || a?.exam_date || 0).getTime();
        const db = new Date(b?.date || b?.exam_date || 0).getTime();
        return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
      });
      return safe;
    };
    // Returns { values: number[], dates: string[] }
    const byAll = (picker) => {
      const vals = [];
      const ds = [];
      for (const e of sortAscByDate(examsAll)) {
        const v = picker(e);
        if (v != null) {
          vals.push(Number(v));
          ds.push(e.date);
        }
      }
      return { values: vals, dates: ds };
    };
    if (kind === 'score') {
      const all = byAll(e => e.total_score);
      if (all.values.length) return all;
      // fallback to last two
      const lastTwoAsc = sortAscByDate(exams);
      const vals = lastTwoAsc.map(e => (e && e.total_score != null ? Number(e.total_score) : null)).filter(v => v != null);
      const ds = lastTwoAsc.map(e => e && e.date).filter(Boolean);
      return { values: vals, dates: ds };
    }
    if (kind === 'net') {
      const all = byAll(e => e.total_net);
      if (all.values.length) return all;
      const lastTwoAsc = sortAscByDate(exams);
      const vals = lastTwoAsc.map(e => (e && e.total_net != null ? Number(e.total_net) : null)).filter(v => v != null);
      const ds = lastTwoAsc.map(e => e && e.date).filter(Boolean);
      if (vals.length) return { values: vals, dates: ds };
      const lastNet = overview && overview.last_exam && (overview.last_exam.total_net != null ? Number(overview.last_exam.total_net) : null);
      return { values: lastNet != null ? [lastNet] : [], dates: lastNet != null ? [latestDate] : [] };
    }
    if (kind === 'rank') {
      // Show school-level ranking only (düşük daha iyidir)
      const rankFields = ['school_rank', 'rank_school', 'overall_rank', 'rank'];
      const vals = [];
      const ds = [];
      for (const e of sortAscByDate(examsAll)) {
        let v = null;
        for (const f of rankFields) {
          if (e && e[f] != null) { v = Number(e[f]); break; }
        }
        if (v != null) { vals.push(v); ds.push(e.date); }
      }
      if (vals.length) return { values: vals, dates: ds };
      // Fallback to last_two if exams_all has no rank
      const lastTwoAsc = sortAscByDate(exams);
      for (const e of lastTwoAsc) {
        let v = null;
        for (const f of rankFields) { if (e && e[f] != null) { v = Number(e[f]); break; } }
        if (v != null) { vals.push(v); ds.push(e.date); }
      }
      if (vals.length) return { values: vals, dates: ds };
      if (overview && overview.last_exam) {
        for (const f of rankFields) {
          if (overview.last_exam[f] != null) return { values: [Number(overview.last_exam[f])], dates: [latestDate] };
        }
      }
      return { values: [], dates: [] };
    }
    return { values: [], dates: [] };
  };

  const seriesObj = buildSeries(metric);
  const series = seriesObj.values;
  const seriesDates = seriesObj.dates;
  const computeAverage = (arr) => arr && arr.length ? (arr.reduce((a, b) => a + b, 0) / arr.length) : null;
  // Trend: difference of last point vs previous
  const computeTrend = (arr, invert = false) => {
    if (!arr || arr.length < 2) return null;
    const d = arr[arr.length - 1] - arr[arr.length - 2];
    return invert ? -d : d;
  };
  const avg = computeAverage(series);
  const trend = computeTrend(series, metric === 'rank');

  const bestWorst = buildBestWorstSubjects({
    latestExam,
    previousExam: (exams && exams.length > 1) ? exams[1] : null,
  });

  const performanceInsight = buildPerformanceInsight({
    latestExam,
    previousExam: (exams && exams.length > 1) ? exams[1] : null,
    examsAll,
  });

  // Build per-subject series using all exams (no subject-level rank)
  const buildSubjectSeries = (kind, subjectName) => {
    const sortAscByDate = (arr) => {
      const safe = (arr || []).slice();
      safe.sort((a, b) => {
        const da = new Date(a?.date || a?.exam_date || 0).getTime();
        const db = new Date(b?.date || b?.exam_date || 0).getTime();
        return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
      });
      return safe;
    };
    const vals = [];
    const ds = [];
    if (!subjectName) return { values: [], dates: [] };
    // Coerce any legacy 'rank' kind to 'net' to avoid empty graphs
    const k = (kind === 'rank') ? 'net' : kind;
    for (const ex of sortAscByDate(examsAll)) {
      const subj = (ex.subjects || []).find(s => s && String(s.name) === String(subjectName));
      if (!subj) continue;
      let v = null;
      if (k === 'correct') v = subj.correct != null ? Number(subj.correct) : null;
      else if (k === 'wrong') v = subj.wrong != null ? Number(subj.wrong) : null;
      else if (k === 'net') v = subj.net != null ? Number(subj.net) : null;
      if (v != null) { vals.push(v); ds.push(ex.date); }
    }
    return { values: vals, dates: ds };
  };
  // Per-subject series will be computed inline when rendering each subject

  // Atoms and charts are now in ./components; sections under ./sections

  const openMiniTasks = () => {
    if (!selectedId) return;
    try {
      window.sessionStorage.setItem(PARENT_MINI_TASK_STUDENT_HINT_KEY, String(selectedId));
    } catch (_) {
      // ignore storage failures; the mini-task entry can still show the picker.
    }
    parentNavigate(`/mini-tasks/parent?student_id=${encodeURIComponent(selectedId)}`);
  };

  const retry = () => setReloadToken((value) => value + 1);
  const failure = <div className="student360-error" role="alert">Bilgi şu anda alınamıyor. <button className="student360-action" onClick={retry}>Yeniden dene</button></div>;
  const showDailyStudy = Number(context?.grade) >= 4 && Number(context?.grade) <= 12;
  const metrics = growthMetrics(context && { ...context, monthlyActiveDays: snapshot ? study?.engagement?.monthly_active_days : null }, entries, verifiedBooks, starGarden);
  const studyTrend = studyComparison(showDailyStudy ? entries : null, context?.today);
  const suggestions = todaySuggestions({ context, entries, books: verifiedBooks, pendingBooks, homeworks: snapshot ? homeworks : null });
  const panel = (id, title, summary, children, extraProps = {}) => <DashboardAccordion key={id} id={`student360-${id}`} title={title} summary={summary} open={openPanel === id} onToggle={() => setOpenPanel((previous) => previous === id ? null : id)} {...extraProps}>{children}</DashboardAccordion>;
  const actOnSuggestion = (suggestion) => {
    if (suggestion.action === 'study') setStudyLogModalRequestToken((value) => value + 1);
    else if (suggestion.action === 'book') setBookModalRequestToken((value) => value + 1);
    else if (suggestion.action === 'reading') openWeeklyReading();
    else setOpenPanel(suggestion.panel);
  };
  const reading = context?.weekly_reading;
  const weekday = context?.today ? new Intl.DateTimeFormat('tr-TR', { timeZone: TURKEY_TIME_ZONE, weekday: 'long' }).format(new Date(`${context.today}T12:00:00Z`)) : '';
  const todayLessons = weeklyPlan?.days?.find((day) => day.day?.toLocaleLowerCase('tr-TR') === weekday.toLocaleLowerCase('tr-TR'))?.lessons;
  const scheduleSummary = errors.snapshot || snapshot?.section_status?.weekly_plan === 'error' ? 'Bilgi şu anda alınamıyor' : todayLessons?.length ? `Bugün ${todayLessons.length} ders • ${todayLessons[0].subject_label}` : 'Haftalık plan ve öğretmenler';
  const assessmentCount = courseAssessmentsAll.length + storybookEvalAll.length + readingSpeedAll.length + schoolExamsAll.length;
  const assessmentSummary = errors.snapshot ? 'Bilgi şu anda alınamıyor' : assessmentCount ? `${assessmentCount} değerlendirme kaydı` : 'Değerlendirmelerini görüntüle';
  const readingSummary = reading?.state === 'pending' ? 'Haftanın okuması bekliyor' : reading?.state === 'completed' ? 'Haftanın okuması tamamlandı' : null;
  const booksSummary = verifiedBooks ? [ `${verifiedBooks.length} bitirilen kitap`, readingSummary ].filter(Boolean).join(' • ') : 'Bilgi şu anda alınamıyor';
  const openWeeklyReading = () => {
    // Keep the existing read-only view available when launch context is unavailable.
    if (context?.role !== 'parent' || reading?.state !== 'pending' || !selectedId || !/^[1-9]\d*$/.test(String(reading?.assignment_id || ''))) {
      setOpenPanel('books');
      return;
    }
    try { window.sessionStorage.setItem(PARENT_MINI_TASK_STUDENT_HINT_KEY, String(selectedId)); } catch (_) {}
    try {
      parentNavigate(`/mini-tasks/parent?student_id=${encodeURIComponent(selectedId)}&weekly_reading_id=${encodeURIComponent(reading.assignment_id)}`);
    } catch (_) {
      setOpenPanel('books');
    }
  };
  return <div style={{ minHeight: '100vh', background: BLUE_PAGE_BG, display: 'flex', justifyContent: 'center', fontFamily: 'Nunito, Inter, system-ui, sans-serif' }}>
    <div style={{ width: '100%', maxWidth: 720, minHeight: '100vh' }}>
      <Header overview={overview} students={students} studentId={selectedId}
        onOpenMiniTasks={context?.role === 'parent' ? openMiniTasks : undefined}
        avatarHref={context?.role === 'parent' && selectedId ? `/avatarim?student_id=${encodeURIComponent(selectedId)}` : undefined}
        pointsHref={selectedId ? `${window.location.pathname.startsWith('/admin/') ? '/admin/puanlarim' : '/puanlarim'}?student_id=${encodeURIComponent(selectedId)}` : undefined}
        paymentsHref={context?.role === 'parent' && paymentSubscriptionIds.includes(String(selectedId)) ? `/odemelerim?student_id=${encodeURIComponent(selectedId)}` : undefined}
        pointBalance={pointBalance?.studentId === String(selectedId) ? pointBalance.value : null} />
      <main style={{ padding: 16, display: 'grid', gap: 12 }}>
        {errors.overview && failure}
        <section className="student360-panel student360-today" aria-labelledby="student360-today-title">
          <div className="student360-today-heading">
            <h2 id="student360-today-title" className="student360-card-title">Bugün</h2>
            <button type="button" className="student360-refresh" onClick={retry} aria-label="Bilgileri yenile" title="Bilgileri yenile"><span aria-hidden="true">↻</span><span>Yenile</span></button>
          </div>
          {errors.overview ? failure : <AttendanceBanner att={att} />}
          <div className="student360-suggestions">
            {(showAllSuggestions ? suggestions.items : suggestions.items.slice(0, 2)).map((suggestion) => {
              const presentation = SUGGESTION_PRESENTATION[suggestion.key];
              return <article key={suggestion.key} className="student360-suggestion" data-kind={suggestion.key} aria-labelledby={`student360-suggestion-${suggestion.key}`}>
                <span className="student360-suggestion-icon" aria-hidden="true">{presentation?.icon || '🌱'}</span>
                <div className="student360-suggestion-copy"><h3 id={`student360-suggestion-${suggestion.key}`}>{presentation?.title || 'Küçük bir adım'}</h3><p>{suggestion.text}</p></div>
                <button type="button" className={`student360-action student360-suggestion-action${context?.role === 'parent' && suggestion.key === 'homework' ? ' student360-homework-pilot' : ''}`} onClick={() => actOnSuggestion(suggestion)}>{suggestion.label}<span aria-hidden="true"> ›</span></button>
              </article>;
            })}
          </div>
          {suggestions.items.length > 2 && <button type="button" className="student360-more" onClick={() => setShowAllSuggestions((value) => !value)}>{showAllSuggestions ? 'Daha az göster' : 'Diğer önerileri göster'}<span aria-hidden="true"> {showAllSuggestions ? '−' : '+'}</span></button>}
          {!suggestions.items.length && <p className="student360-today-empty">{context && context.role !== 'parent' ? 'Öğrenci kayıtlarını görüntüleyebilirsiniz.' : suggestions.complete ? 'Şu an için yeni bir hatırlatmamız yok.' : 'Hatırlatma bilgileri şu anda alınamıyor.'}</p>}
          {errors.context && failure}
        </section>
        {errors.snapshot ? failure : <FutureForestCard forest={forest} studentName={studentFirstName} hasTodayContribution={hasTodayContribution} canContribute={context?.role === 'parent'} />}
        {panel('growth', 'Gelişim Özeti', 'Katılım, çalışma grafikleri ve günlük kayıtlar', <>
          {errors.snapshot ? failure : <><ParentEngagementTopCard comparison={studyTrend} showStudyStats={showDailyStudy} study={study} studentName={studentFirstName} today={context?.today} hasTodayStudyLog={hasTodayStudyLog} onOpenStudyLogModal={showDailyStudy && context?.can_write_study ? () => setStudyLogModalRequestToken((value) => value + 1) : undefined} /><ParentEngagementCommentCard comparison={studyTrend} study={study} studentName={studentFirstName} canWrite={context?.role === 'parent'} /></>}
          {showDailyStudy && <div ref={setStudyMount} />}
          {selectedId && <LearningDevelopment key={selectedId} studentId={selectedId} active={openPanel === 'growth'} refreshKey={reloadToken} />}
        </>, {
          toggleLabel: openPanel === 'growth' ? 'Gelişim detaylarını gizle' : 'Gelişim detaylarını gör',
          preview: <div className="student360-metrics">{(metrics.length ? metrics : [1, 2, 3].map(() => ({ label: 'Bilgi şu anda alınamıyor', value: '—' }))).map((item, index) => <div className="student360-metric" key={index}>
            <strong className={typeof item.value === 'number' ? 'student360-metric-number' : 'student360-metric-status'} title={String(item.value)} aria-label={typeof item.value === 'string' ? item.value : undefined}>{COMPACT_METRIC_VALUES[item.value] || item.value}</strong><small>{item.label}</small>
          </div>)}</div>,
        })}
        <h2 style={{ fontSize: 20, color: '#0f1e56', margin: '8px 0 0' }}>Detaylı Bilgiler</h2>
        {panel('homeworks', 'Ödevlerim', snapshot ? `${homeworks.length} aktif ödev kaydı` : 'Bilgi şu anda alınamıyor', errors.snapshot ? failure : <>{!homeworks.length && !snapshot?.homeworks_stats?.total && <p>Henüz ödev kaydı yok.</p>}<Homeworks homeworks={homeworks} stats={snapshot?.homeworks_stats} formatDate={formatDate} today={context?.today} /></>)}
        {panel('books', 'Okumalarım ve Kütüphanem', booksSummary, <>
          {errors.context ? failure : <Card title="Haftalık Okumalar"><p>{reading?.title || ({ not_assigned: 'Bu hafta okuma atanmadı.', not_applicable: 'Haftalık okuma bu sınıf ve dönem için uygulanmıyor.', unknown: 'Bilgi şu anda alınamıyor.' }[reading?.state]) || 'Bilgi şu anda alınamıyor.'}</p>{reading?.state === 'completed' && <p>Tamamlandı</p>}{reading?.state === 'pending' && <p>Bekliyor</p>}{reading?.state === 'pending' && context?.role === 'parent' && <button className="student360-action" onClick={openWeeklyReading}>Okumayı başlat</button>}</Card>}
          <div ref={setBooksMount} />
                    {libraryView && (
            <Card title="Kütüphanem">
              <div
                style={{
                  background: '#dbeaf7',
                  border: '1px solid #c3d7ea',
                  borderRadius: 18,
                  padding: 16,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div role="img" aria-label={`${libraryView.fullName} avatarı`}
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                      flex: '0 0 auto',
                      background: libraryView.avatar.bgColor,
                      border: `2px solid ${libraryView.avatar.borderColor}`,
                      fontSize: 24,
                    }}
                  >
                    {libraryView.avatar.imageUrl
                      ? <img src={libraryView.avatar.imageUrl} alt="" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : libraryView.avatar.avatar}
                  </div>
                  <div style={{ fontWeight: 900, fontSize: 24, color: '#1f2937' }}>{libraryView.fullName || '-'}</div>
                </div>

                <div style={{ textAlign: 'center', fontSize: 22, fontWeight: 800, color: '#1f2937', marginTop: 8 }}>
                  {libraryView.pages} sayfa • {libraryView.books} kitap
                </div>

                <div style={{ textAlign: 'center', marginTop: 8, fontSize: 30, fontWeight: 800, color: '#4b5563' }}>
                  {libraryView.levelLabel}
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginTop: 10 }}>
                  {Array.from({ length: libraryView.gradeKnown ? 7 : 0 }).map((_, idx) => (
                    <span
                      key={idx}
                      style={{
                        fontSize: 'clamp(24px, 7vw, 34px)',
                        lineHeight: 1,
                        filter: idx < libraryView.filledStars ? 'none' : 'grayscale(1) opacity(0.65)',
                      }}
                    >
                      ⭐
                    </span>
                  ))}
                  {libraryView.overflowStars > 0 && (
                    <span style={{ marginLeft: 4, fontWeight: 800, fontSize: 'clamp(20px, 6vw, 28px)', color: '#6b7280', lineHeight: 1 }}>
                      +{libraryView.overflowStars}
                    </span>
                  )}
                </div>
                {libraryView.gradeKnown && libraryView.filledStars === 0 && libraryView.overflowStars === 0 && (
                  <div style={{ textAlign: 'center', marginTop: 4, color: '#64748b', fontWeight: 700, fontSize: 14 }}>
                    0 yıldız
                  </div>
                )}

                {libraryView.badge && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        borderRadius: 999,
                        padding: '6px 12px',
                        background: 'rgba(255,255,255,0.75)',
                        border: '1px solid #dbe2ea',
                        fontWeight: 800,
                        color: '#1f2937',
                        fontSize: 18,
                      }}
                    >
                      {libraryView.badge.icon} {libraryView.badge.label} x{libraryView.badge.mult}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          )}


        </>)}
        {panel('assessments', 'Değerlendirmelerim', assessmentSummary, errors.snapshot ? failure : <>          <CeteleReport studentId={selectedId} refreshKey={reloadToken} />
          <CourseAssessmentReport
            items={courseAssessmentsAll}
            selectedId={selectedGeneralEvalId}
            setSelectedId={setSelectedGeneralEvalId}
            formatDate={formatDate}
          />

          <StorybookEvaluationReport
            items={storybookEvalAll}
            selectedId={selectedStorybookEvalId}
            setSelectedId={setSelectedStorybookEvalId}
            formatDate={formatDate}
          />

          <ReadingSpeedReport
            items={readingSpeedAll}
            selectedId={selectedReadingSpeedId}
            setSelectedId={setSelectedReadingSpeedId}
            formatDate={formatDate}
          />

          <SchoolExamReport
            items={schoolExamsAll}
            formatDate={formatDate}
          />

</>)}
        {hasMockExamData && panel('mocks', 'Deneme Sonuçlarım', `${examsAll.length} sınav kaydı`, <>
            <PerformanceSummary
              metric={metric}
              setMetric={setMetric}
              series={series}
              seriesDates={seriesDates}
              avg={avg}
              trend={trend}
              bestWorst={bestWorst}
              analysisInsight={performanceInsight}
            />

          {/* Ders Performansı */}
          <SubjectPerformance
            allSubjects={allSubjects}
            subjectMetric={subjectMetric}
            setSubjectMetric={setSubjectMetric}
            buildSubjectSeries={buildSubjectSeries}
            computeAverage={computeAverage}
          />

          <ExamReport
            examsAll={examsAll}
            selectedExamId={selectedExamId}
            setSelectedExamId={setSelectedExamId}
            formatDate={formatDate}
          />

</>)}
        {panel('stars', 'Yıldız Bahçem', starGarden ? `${starGarden.total} güzel davranış yıldızı` : 'Bilgi şu anda alınamıyor', errors.stars ? failure : starGarden ? <>            <Card title="Yıldız Bahçem">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 10 }}>
                <div style={{ color: '#64748b', fontSize: 13 }}>Her davranışın kendi çiçeği</div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: BLUE_SOFT_SURFACE, border: '1px solid #dbeafe', padding: '6px 10px', borderRadius: 999 }}>
                  <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>Toplam:</span>
                  <span style={{ fontWeight: 900, color: BLUE_PRIMARY_DARK, fontVariantNumeric: 'tabular-nums' }}>{starGarden.total}</span>
                  <span style={{ color: BLUE_PRIMARY_DARK, fontWeight: 900 }}>⭐</span>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10 }}>
                {starGarden.items.map((item, index) => (
                  <div key={item.criteria_id ?? index} style={{ display: 'flex', alignItems: 'center', gap: 8,
                    background: '#f7fee7', border: '1px solid #d9f99d', borderRadius: 12, padding: 10 }}>
                    <span aria-hidden="true" style={{ fontSize: 31 }}>{GARDEN_FLOWERS[index % GARDEN_FLOWERS.length]}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, overflowWrap: 'anywhere' }}>{item.name}</div>
                      <div style={{ fontWeight: 900, color: '#92400e' }}>⭐ {Number(item.count || 0)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
{!starGarden.items.length && <p>Henüz güzel davranış yıldızı kaydı yok.</p>}</> : failure)}
        {panel('school', 'Okul Yaşamım', 'Yoklama, beslenme ve gözlem günlüğü', <>
          {errors.snapshot || snapshot?.section_status?.attendance === 'error' ? failure : <AttendanceSummary stats={snapshot?.attendance_stats} />}
          {errors.snapshot || snapshot?.section_status?.nutrition === 'error' ? failure : <NutritionAssessments stats={snapshot?.nutrition_stats} />}
          {errors.observations ? failure : <>          <Card title="Gözlem Günlüğü">
            {displayedObservations.length === 0 ? (
              <div style={{ color: '#64748b', fontSize: 14 }}>Henüz gözlem kaydı paylaşılmadı.</div>
            ) : (
              displayedObservations.map((entry, idx) => (
                <div
                  key={entry.entry_id || idx}
                  style={{
                    padding: '10px 0',
                    borderBottom: idx === displayedObservations.length - 1 ? 'none' : '1px solid #f1f5f9',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <div style={{ fontWeight: 600 }}>{entry.recorded_by_name || 'Öğretmen'}</div>
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {formatObservationDate(entry.parent_notified_at || entry.recorded_at)}
                    </span>
                  </div>
                  <div style={{ fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap' }}>{entry.review_text}</div>
                  {entry.parent_notified_at && (
                    <div style={{ fontSize: 12, color: '#22c55e', marginTop: 6 }}>
                      Velilere paylaşıldı: {formatObservationDate(entry.parent_notified_at)}
                    </div>
                  )}
                </div>
              ))
            )}
            {Array.isArray(observations) && observations.length > displayedObservations.length && (
              <button className="student360-action" onClick={() => setObservationLimit((value) => value + 5)}>Daha fazla göster</button>
            )}
          </Card>

</>}
        </>)}
        {panel('schedule', 'Ders Programım', scheduleSummary, errors.snapshot || snapshot?.section_status?.weekly_plan === 'error' ? failure : <>          {weeklyPlan && weeklyPlan.days && weeklyPlan.days.length > 0 && (
            <Card title="Haftalık Plan">
              {weeklyPlan.days.map((d, i) => (
                <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{d.day}</div>
                  {d.lessons.map((l, j) => (
                    <div
                      key={j}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(72px, 96px) minmax(0, 1fr) minmax(84px, 120px)',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14,
                        padding: '4px 0'
                      }}
                    >
                      <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{l.start}-{l.end}</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 2 }}>{l.subject_label}</span>
                      <span style={{ color: '#64748b', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.teacher_name}</span>
                    </div>
                  ))}
                </div>
              ))}
            </Card>
          )}

          {teacherSummary && teacherSummary.entries && teacherSummary.entries.length > 0 && (
            <Card title="Öğretmenler">
              {teacherSummary.entries.map((t, i) => (
                <Row key={i} label={t.subject_label} value={t.teacher_name} />
              ))}
            </Card>
          )}

{!weeklyPlan?.days?.length && <p>Henüz ders programı kaydı yok.</p>}</>)}
      </main>
      {selectedId && <StudySummary key={`${selectedId}-${reloadToken}`} study={snapshot ? study : null} studentId={selectedId} today={context?.today}
        splitLayout studyMount={showDailyStudy && openPanel === 'growth' ? studyMount : null} booksMount={openPanel === 'books' ? booksMount : null}
        canWriteStudy={!!context?.can_write_study} canWriteBooks={!!context?.can_write_books}
        requestOpenStudyLogModalToken={studyLogModalRequestToken} requestOpenBookModalToken={bookModalRequestToken}
        onEntriesLoaded={setEntries} onPendingBooksLoaded={setPendingBooks} onStudyChanged={() => loadSnapshot(selectedId)} />}
      <LegalFooter variant="student" />
    </div>
  </div>;
}
